import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  TextInput,
  Select,
  Badge
} from '@etherisc/ui-kit';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getCurrencyOptions } from '../../config/products';
import { getAvailableQuotationScripts, getAvailableClaimsScripts } from '../../services/quotationService';
import { CronScheduleWidget, cronToHumanReadable } from '../shared/CronScheduleWidget';
import type { AutomationConfig, AutomationMode } from '../../types/product';

interface OverviewFormProps {
  productName: string;
  productCode: string;
  productDescription?: string;
  productStatus: string;
  productTags: string[];
  productVersion?: string;
  productCurrency?: string;
  quotationScript?: string;
  claimsScript?: string;
  onFieldChange: (field: string, value: unknown) => void;
  readOnly?: boolean;
  automationConfig?: AutomationConfig;
  onAutomationConfigChange?: (config: AutomationConfig) => void;
  cronExpression?: string;
  onCronExpressionChange?: (cron: string) => void;
}

const PRODUCT_STATUSES = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'ARCHIVED', label: 'Archived' }
];

const AUTOMATION_MODE_OPTIONS = [
  { value: 'manual', label: 'Manual' },
  { value: 'automated', label: 'Automated' },
];

export function OverviewForm({
  productName,
  productCode,
  productDescription,
  productStatus,
  productTags,
  productVersion,
  productCurrency,
  quotationScript,
  claimsScript,
  onFieldChange,
  readOnly = false,
  automationConfig,
  onAutomationConfigChange,
  cronExpression,
  onCronExpressionChange,
}: OverviewFormProps) {
  const [newTag, setNewTag] = useState('');
  const [availableScripts, setAvailableScripts] = useState<string[]>([]);
  const [availableClaimsScripts, setAvailableClaimsScripts] = useState<string[]>([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const currencyOptions = getCurrencyOptions();

  useEffect(() => {
    const fetchScripts = async () => {
      setLoadingScripts(true);
      try {
        const [quotScripts, clmScripts] = await Promise.all([
          getAvailableQuotationScripts(),
          getAvailableClaimsScripts(),
        ]);
        setAvailableScripts(quotScripts);
        setAvailableClaimsScripts(clmScripts);
      } catch (error) {
        console.error('Failed to fetch scripts:', error);
        setAvailableScripts(['maize_quotation@1.0.0']);
        setAvailableClaimsScripts(['maize_claims@1.0.0', 'maize_claims@1.1.0']);
      } finally {
        setLoadingScripts(false);
      }
    };

    fetchScripts();
  }, []);

  const handleAddTag = () => {
    if (!newTag.trim() || readOnly) return;
    const newTags = [...productTags, newTag.trim()];
    onFieldChange('tags', newTags);
    setNewTag('');
  };

  const handleRemoveTag = (index: number) => {
    if (readOnly) return;
    const newTags = productTags.filter((_, i) => i !== index);
    onFieldChange('tags', newTags);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <TextInput
              label="Product Name"
              value={productName}
              onChange={(e) => onFieldChange('name', e.target.value)}
              disabled={readOnly}
              required
            />
            <TextInput
              label="Product Code"
              value={productCode}
              onChange={(e) => onFieldChange('code', e.target.value)}
              disabled={readOnly}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={productDescription || ''}
              onChange={(e) => onFieldChange('description', e.target.value)}
              disabled={readOnly}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Enter product description..."
            />
          </div>

          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <Select
                value={productStatus}
                onValueChange={(value) => onFieldChange('status', value)}
                disabled={readOnly}
                options={PRODUCT_STATUSES.map((status) => ({
                  value: status.value,
                  label: status.label
                }))}
                placeholder="Select status"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
              </label>
              <Select
                value={productCurrency || ''}
                onValueChange={(value) => onFieldChange('currency', value)}
                disabled={readOnly}
                options={currencyOptions.map((currency) => ({
                  value: currency.value,
                  label: currency.label
                }))}
                placeholder="Select currency"
              />
            </div>
            {productVersion && (
              <TextInput
                label="Version"
                value={productVersion}
                onChange={(e) => onFieldChange('version', e.target.value)}
                disabled={readOnly}
              />
            )}
          </div>

          {/* Quotation Script Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quotation Script
            </label>
            <Select
              value={quotationScript || ''}
              onValueChange={(value) => onFieldChange('quotationScript', value)}
              disabled={readOnly || loadingScripts}
              options={Array.isArray(availableScripts) ? availableScripts.map((script) => ({
                value: script,
                label: script
              })) : []}
              placeholder={loadingScripts ? "Loading scripts..." : "Select quotation script"}
            />
            <p className="text-xs text-gray-500 mt-1">
              Select the R-Server quotation script to use for calculations
            </p>
          </div>

          {/* Claims Script Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Claims Script
            </label>
            <Select
              value={claimsScript || ''}
              onValueChange={(value) => onFieldChange('claimsScript', value)}
              disabled={readOnly || loadingScripts}
              options={Array.isArray(availableClaimsScripts) ? availableClaimsScripts.map((script) => ({
                value: script,
                label: script
              })) : []}
              placeholder={loadingScripts ? "Loading scripts..." : "Select claims script"}
            />
            <p className="text-xs text-gray-500 mt-1">
              Select the R-Server claims script to use for assessment calculations
            </p>
          </div>

          {/* Tags Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Product Tags</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {productTags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-2">
                  {tag}
                  {!readOnly && (
                    <button
                      onClick={() => handleRemoveTag(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon size={14} />
                    </button>
                  )}
                </Badge>
              ))}
              {productTags.length === 0 && (
                <span className="text-gray-500 text-sm">No tags added</span>
              )}
            </div>
            {!readOnly && (
              <div className="flex gap-2">
                <TextInput
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter tag name"
                  className="flex-1"
                />
                <button
                  onClick={handleAddTag}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <PlusIcon size={16} className="mr-1 inline" />
                  Add
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Claims Automation */}
      <Card>
        <CardHeader>
          <CardTitle>Claims Automation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 mb-2">
            Configure which stages of the claims pipeline run automatically for this product.
          </p>

          {/* Assess */}
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm font-medium text-gray-700">Assess</label>
            <div className="w-40">
              <Select
                value={automationConfig?.assess?.mode ?? 'manual'}
                onValueChange={(v) => {
                  if (readOnly || !onAutomationConfigChange) return;
                  onAutomationConfigChange({
                    ...(automationConfig ?? { assess: { mode: 'manual' }, approve: { mode: 'manual' }, queue: { mode: 'manual' } }),
                    assess: { mode: v as AutomationMode },
                  });
                }}
                options={AUTOMATION_MODE_OPTIONS}
                disabled={readOnly}
              />
            </div>
            {automationConfig?.assess?.mode === 'automated' && onCronExpressionChange && (
              <CronScheduleWidget
                value={cronExpression ?? ''}
                onChange={onCronExpressionChange}
                disabled={readOnly}
              />
            )}
            {automationConfig?.assess?.mode === 'automated' && !onCronExpressionChange && cronExpression && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  {cronToHumanReadable(cronExpression)}
                </span>
                <span className="text-xs text-gray-400 font-mono">
                  ({cronExpression})
                </span>
              </div>
            )}
          </div>

          {/* Approve */}
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm font-medium text-gray-700">Approve</label>
            <div className="w-40">
              <Select
                value={automationConfig?.approve?.mode ?? 'manual'}
                onValueChange={(v) => {
                  if (readOnly || !onAutomationConfigChange) return;
                  onAutomationConfigChange({
                    ...(automationConfig ?? { assess: { mode: 'manual' }, approve: { mode: 'manual' }, queue: { mode: 'manual' } }),
                    approve: { mode: v as AutomationMode },
                  });
                }}
                options={AUTOMATION_MODE_OPTIONS}
                disabled={readOnly}
              />
            </div>
            {automationConfig?.approve?.mode === 'automated' && (
              <span className="text-xs text-gray-500">Runs immediately after assessment</span>
            )}
          </div>

          {/* Queue */}
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm font-medium text-gray-700">Queue</label>
            <div className="w-40">
              <Select
                value={automationConfig?.queue?.mode ?? 'manual'}
                onValueChange={(v) => {
                  if (readOnly || !onAutomationConfigChange) return;
                  onAutomationConfigChange({
                    ...(automationConfig ?? { assess: { mode: 'manual' }, approve: { mode: 'manual' }, queue: { mode: 'manual' } }),
                    queue: { mode: v as AutomationMode },
                  });
                }}
                options={AUTOMATION_MODE_OPTIONS}
                disabled={readOnly}
              />
            </div>
            {automationConfig?.queue?.mode === 'automated' && (
              <span className="text-xs text-gray-500">Runs immediately after approval</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
